import d3 from 'd3';
import isObject from 'lodash/isObject';
import get from 'lodash/get';

const ethnicLabels = {
  international: 'International Students',
  africanAmerican: 'Black / African American',
  hispanic: 'Hispanic / Latino',
  nativePacificIslander: 'Native Pacific Islander',
  twoOrMoreRaces: 'Two or more races',
  caucasian: 'White / Caucasian',
  asian: 'Asian',
  nativeAmerican: 'Native American',
  nonResidentAlien: 'Non-resident Alien',
  nativeUnknown: 'Ethnicity Unknown',
};

export const COLORS = [
  '#35C4BB',
  '#D20261',
  '#33367A',
  '#FEB44B',
  '#B540FF',
  '#696EF5',
  '#8BC100',
  '#0074D9',
  '#003738',
  '#4E4F65',
  '#1C1D39',
  '#4E4F65',
];

const ETHNICITY_COLORS = {
  caucasian: '#35C4BB',
  asian: '#D20261',
  nativePacificIslander: '#33367A',
  twoOrMoreRaces: '#FEB44B',
  hispanic: '#B540FF',
  africanAmerican: '#696EF5',
  nativeUnknown: '#8BC100',
  nativeAmerican: '#0074D9',
};

export const CAMINO_COLORS = ['#35C4BB', '#045859'];

export const CAMINO_ETHNICITY_COLORS = {
  caucasian: '#35C4BB',
  asian: '#F23D61',
  nativePacificIslander: '#33367A',
  twoOrMoreRaces: '#33367A',
  hispanic: '#FFAF40',
  africanAmerican: '#696EF5',
  nativeUnknown: '#045859',
  nativeAmerican: '#0074D9',
};

const sortChartData = (chartData1, chartData2) => {
  const diff = chartData2.value - chartData1.value;

  if (diff === 0) {
    return chartData1.line1.localeCompare(chartData2.line1);
  }

  return diff;
};

export const formatEthnicityData = (ethnicity, isCamino) => {
  const ethnicityData = [];

  if (!isObject(ethnicity)) return ethnicityData;

  const colors = isCamino ? CAMINO_ETHNICITY_COLORS : ETHNICITY_COLORS;

  Object.keys(ethnicity).forEach((key) => {
    if (key !== 'nonResidentAlien' && key !== 'international') {
      const item = ethnicity[key];
      if (
        isObject(item) &&
        'value' in item &&
        key in ethnicLabels &&
        Number(item.value) > 0
      ) {
        ethnicityData.push({
          value: Number(item.value),
          line1: ethnicLabels[key],
          line2: `${item.value}%`,
          color: colors[key],
        });
      }
    }
  });

  ethnicityData.sort((chartData1, chartData2) => {
    return sortChartData(chartData1, chartData2);
  });

  return ethnicityData;
};

export const formatDonutHalves = (percentages, legends, isCamino) => {
  const chartData = [];

  if (!percentages || !legends || percentages.length !== 2 || legends.length !== 2) {
    return chartData;
  }

  const colors = isCamino ? CAMINO_COLORS : COLORS;

  percentages.forEach((perc, index) => {
    if (perc || perc === 0) {
      chartData.push({
        value: perc,
        line1: legends[index],
        line2: `${perc}%`,
        color: colors[index],
      });
    }
  });

  return chartData;
};

export const getTotalUndergraduateEnrollment = (data) => {
  if (!isObject(data)) return 0;

  if (
    'men' in data &&
    'women' in data &&
    'value' in data.men &&
    'value' in data.women &&
    !isNaN(data.men.value) &&
    !isNaN(data.women.value)
  ) {
    return Number(data.men.value) + Number(data.women.value);
  }

  return 0;
};

export const getEnrollmentData = (schoolSize) => {
  const undergradEnrollment =
    parseInt(get(schoolSize, 'undergraduateWomen.value', '0'), 10) +
    parseInt(get(schoolSize, 'undergraduateMen.value', '0'), 10);
  const gradEnrollment =
    parseInt(get(schoolSize, 'graduateWomen.value', '0'), 10) +
    parseInt(get(schoolSize, 'graduateMen.value', '0'), 10);

  const totalEnrollment = undergradEnrollment + gradEnrollment;

  return [undergradEnrollment, gradEnrollment, totalEnrollment];
};

export const formatValueAsPercent = (value) => `${value}%`;

const numberWithCommas = (x) => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const donutCallback = (elementId, totalUndergraduateEnrollment, studentLabels) => {
  const svg = d3.select(elementId);

  /* istanbul ignore next */
  const donut = svg.selectAll('g.nv-pie').filter((_, i) => i === 1);

  // inserting text into donut hole
  donut
    .insert('text', 'g')
    .text(numberWithCommas(totalUndergraduateEnrollment))
    .attr('class', 'donut-middle')
    .attr('text-anchor', 'middle')
    .attr('dy', '-.55em')
    .style('fill', '#000');

  let i = 1;
  studentLabels.forEach((label) => {
    const dy = 0.55 * i;

    donut
      .append('text', 'g')
      .text(label)
      .attr('class', 'donut-middle-subtext')
      .attr('text-anchor', 'middle')
      .attr('dy', dy.toString() + 'em');
    i += 2;
  });
};

export const donutTooltip = {
  enabled: true,
  keyFormatter: (key) => `${key}: `,
  valueFormatter: formatValueAsPercent,
};
